import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Search, Settings2, Bot, AlertOctagon, Mail, Trash2 } from "lucide-react";
import OutreachEmailPanel from "./OutreachEmailPanel";
import OutreachQueue from "./OutreachQueue";
import OutreachSettings from "./OutreachSettings";
import StoppedList from "./StoppedList";
import DiscardDialog from "./DiscardDialog";
import { S, MANROPE, FOCUS, API, STATUS_LABEL, STATUS_TONE, auDateTime, relative } from "./lib";

const FILTERS = [["awaiting_approval", "Awaiting approval"], ["draft", "Drafts"],
  ["sent", "Sent"], ["discarded", "Binned"], ["all", "Everything"]];

const Stat = ({ label, value, tone, onClick, testid, active }) => (
  <button onClick={onClick} data-testid={testid}
    className={`text-left px-3.5 py-2.5 rounded-[9px] transition-colors ${FOCUS} hover:bg-[#FBFAF7]`}
    style={{ background: active ? "#FFFBEB" : S.card,
      border: `1px solid ${active ? S.accentEdge : S.border}` }}>
    <div className="text-[10px] font-bold uppercase tracking-[0.09em]" style={{ color: S.ink3 }}>{label}</div>
    <div className="mono text-[17px] font-bold" style={{ color: tone || S.ink }}>{value}</div>
  </button>
);

export default function OutreachInbox() {
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({});
  const [status, setStatus] = useState("awaiting_approval");
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(null);
  const [settings, setSettings] = useState(false);
  const [sending, setSending] = useState({});
  const [loading, setLoading] = useState(true);
  const [stamp, setStamp] = useState(0);
  const [binning, setBinning] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/emails`, { params: { status, q } });
      setRows(data.emails || []);
      setCounts(data.counts || {});
      setSending(data.sending || {});
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load the outreach emails");
    } finally { setLoading(false); }
  }, [status, q]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-4" data-testid="outreach-inbox">
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <h2 className="text-[22px] font-extrabold tracking-[-0.02em]" style={{ ...MANROPE, color: S.ink }}>
            Outreach
          </h2>
          <p className="text-[12.5px] mt-0.5" style={{ color: S.ink2 }}>
            Emails drafted here or by Claude. Nothing reaches a prospect until the rep who owns
            them approves it — approving is what sends.
          </p>
        </div>
        <button onClick={() => setSettings(true)} data-testid="outreach-settings-open"
          className={`ml-auto h-9 px-3 rounded-[7px] inline-flex items-center gap-1.5 text-[11.5px] font-bold uppercase ${FOCUS}`}
          style={{ background: S.card, border: `1px solid ${S.border}`, color: S.ink2, ...MANROPE }}>
          <Settings2 className="w-3.5 h-3.5" /> Rules
        </button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
        <Stat label="Awaiting approval" value={counts.awaiting_approval ?? 0} tone={S.accentInk}
          active={status === "awaiting_approval"} testid="outreach-stat-awaiting"
          onClick={() => setStatus("awaiting_approval")} />
        <Stat label="Drafts" value={counts.draft ?? 0} active={status === "draft"}
          testid="outreach-stat-drafts" onClick={() => setStatus("draft")} />
        <Stat label="Sent" value={counts.sent ?? 0} tone={S.good} active={status === "sent"}
          testid="outreach-stat-sent" onClick={() => setStatus("sent")} />
        <Stat label="Blocked by rules" value={counts.blocked ?? 0} tone={S.bad}
          testid="outreach-stat-blocked" onClick={() => setStatus("all")} />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_380px] gap-4">
        <div className="rounded-[11px] overflow-hidden" style={{ background: S.card, border: `1px solid ${S.border}` }}>
          <div className="px-4 py-3 flex flex-wrap items-center gap-2" style={{ borderBottom: `1px solid ${S.divider}` }}>
            <div className="flex items-center gap-1">
              {FILTERS.map(([key, label]) => (
                <button key={key} onClick={() => setStatus(key)} data-testid={`outreach-filter-${key}`}
                  className={`h-8 px-2.5 rounded-[6px] text-[11px] font-bold uppercase tracking-[0.04em] ${FOCUS}`}
                  style={{ background: status === key ? S.accent : S.panel,
                    border: `1px solid ${status === key ? S.accentEdge : S.border}`,
                    color: status === key ? "#000" : S.ink2, ...MANROPE }}>
                  {label}
                </button>
              ))}
            </div>
            <div className="ml-auto relative">
              <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2" style={{ color: S.muted }} />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search"
                data-testid="outreach-search"
                className={`h-8 pl-8 pr-2.5 rounded-[6px] text-[12px] w-[200px] ${FOCUS}`}
                style={{ background: S.panel, border: `1px solid ${S.border}`, color: S.ink }} />
            </div>
          </div>

          {loading && <div className="px-4 py-8 text-[12px]" style={{ color: S.ink3 }}>Loading…</div>}
          {!loading && rows.length === 0 && (
            <div className="px-4 py-10 text-center" data-testid="outreach-empty">
              <Mail className="w-7 h-7 mx-auto mb-2" style={{ color: S.muted }} />
              <div className="text-[12.5px]" style={{ color: S.ink2 }}>
                Nothing here. Draft one from the queue on the right, or ask Claude to draft a few.
              </div>
            </div>
          )}

          {!loading && rows.map((r) => {
            const tone = STATUS_TONE[r.status] || STATUS_TONE.draft;
            return (
              <button key={r.id} onClick={() => setOpen(r.id)} data-testid={`outreach-row-${r.id}`}
                className={`w-full text-left px-4 py-3 flex items-start gap-3 transition-colors hover:bg-[#FBFAF7] ${FOCUS}`}
                style={{ borderBottom: `1px solid ${S.divider}` }}>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="mono text-[11px] font-bold" style={{ color: S.ink3 }}>{r.ref}</span>
                    <span className="px-1.5 py-[2px] rounded-[4px] text-[9.5px] font-bold uppercase tracking-[0.07em]"
                      style={{ background: tone.bg, border: `1px solid ${tone.edge}`, color: tone.ink }}>
                      {STATUS_LABEL[r.status]}
                    </span>
                    {r.drafted_by_claude && <Bot className="w-3.5 h-3.5" style={{ color: S.accentInk }} title="Drafted by Claude" />}
                    {r.blocking_flags?.length > 0 && (
                      <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase" style={{ color: S.bad }}>
                        <AlertOctagon className="w-3 h-3" /> {r.blocking_flags.length} blocking
                      </span>
                    )}
                    <span className="ml-auto text-[10.5px]" style={{ color: S.muted }}>
                      {r.status === "sent" ? auDateTime(r.sent_at) : relative(r.updated_at)}
                    </span>
                    {r.status !== "sent" && r.status !== "discarded" && (
                      <span role="button" tabIndex={0} data-testid={`outreach-bin-${r.id}`}
                        title="Bin this draft"
                        onClick={(e) => { e.stopPropagation(); setBinning(r); }}
                        onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); setBinning(r); } }}
                        className={`h-6 w-6 rounded-[5px] grid place-items-center ${FOCUS}`}
                        style={{ background: S.panel, border: `1px solid ${S.border}` }}>
                        <Trash2 className="w-3 h-3" style={{ color: S.bad }} />
                      </span>
                    )}
                  </div>
                  <div className="text-[13px] font-semibold truncate mt-0.5" style={{ color: S.ink }}>
                    {r.subject || "No subject yet"}
                  </div>
                  <div className="text-[11px] truncate" style={{ color: S.ink3 }}>
                    {r.prospect_name || "—"} · {r.to_email || "no address"} · as {r.from_mailbox || "—"}
                    {r.assigned_rep ? ` · ${r.assigned_rep} approves` : ""}
                  </div>
                  {r.discard_reason && (
                    <div className="text-[11px] mt-1" style={{ color: "#8E3A12" }}>
                      Binned: {r.discard_reason}
                    </div>
                  )}
                  {r.sent_back_note && (
                    <div className="text-[11px] mt-1" style={{ color: "#8E3A12" }}>
                      Sent back: {r.sent_back_note}
                    </div>
                  )}
                </div>
              </button>
            );
          })}
        </div>

        <div className="space-y-4">
          <OutreachQueue key={`q${stamp}`} onDrafted={(id) => { setOpen(id); load(); }} />
          <StoppedList refreshKey={stamp} onChange={() => setStamp((n) => n + 1)} />
          <div className="p-4 rounded-[11px] text-[11.5px] leading-relaxed"
            style={{ background: S.card, border: `1px solid ${S.border}`, color: S.ink2 }}>
            <div className="text-[12px] font-bold mb-1" style={{ ...MANROPE, color: S.ink }}>
              Asking Claude
            </div>
            “Draft follow-ups for everyone due this week, as Kevin.” Claude reads the prospect,
            writes the email and puts it up for approval. It cannot approve and it cannot send.
            {sending.saves_to_sent_items === false && (
              <div className="mt-2" style={{ color: S.ink3 }}>
                Sending through {sending.provider === "resend" ? "Resend" : "Microsoft 365"}. The sending mailbox is BCC'd until Microsoft
                365 sending is switched on, which is what puts a copy in Outlook Sent Items.
              </div>
            )}
          </div>
        </div>
      </div>

      {open && <OutreachEmailPanel emailId={open} onClose={() => setOpen(null)} onSaved={load} />}
      {settings && <OutreachSettings onClose={() => { setSettings(false); load(); }} />}
      {binning && (
        <DiscardDialog email={binning} onClose={() => setBinning(null)}
          onConfirm={async ({ reason, scope, days }) => {
            try {
              const { data } = await axios.post(`${API}/emails/${binning.id}/discard`,
                { reason, scope, days });
              toast.message(data.note);
              setBinning(null);
              setStamp((n) => n + 1);
              load();
            } catch (e) {
              toast.error(e?.response?.data?.detail || "Could not bin that");
            }
          }} />
      )}
    </div>
  );
}
